/**
 * ActivityTracker
 *
 * Monitors real browser activity (mouse / keyboard / scroll / touch).
 *
 *  30 min idle  → popup "Are you still working?"  (with 60-min countdown)
 *  90 min idle  → auto clock-out
 *  Any activity → reset timer; if was clocked-out → auto clock-in
 *
 * Only activates when the logged-in staff has /staff-tracking readPermission.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Progress, Space, notification } from 'antd';
import {
  ClockCircleOutlined, CheckCircleOutlined, PoweroffOutlined,
} from '@ant-design/icons';
import { hasPermission } from '../permissions';
import { getCurrentStaffId } from '../../authHelper';
import { clockIn, clockOut, getCurrentStatus } from '../../pages/sn_work_tracking/workTrackingService';

const IDLE_WARNING_MIN  = 30;   // show popup after 30 min idle
const IDLE_CLOCKOUT_MIN = 90;   // auto clock-out after 90 min idle
const TICK_MS           = 30000; // check every 30 seconds

export default function ActivityTracker() {
  const staffId = getCurrentStaffId();
  if (!staffId || !hasPermission('/staff-tracking', 'readPermission')) return null;
  return <Tracker staffId={staffId} />;
}

function Tracker({ staffId }) {
  const lastActivityRef  = useRef(Date.now());
  const clockedOutRef    = useRef(false);  // track if WE auto-clocked-out
  const [showWarning, setShowWarning]   = useState(false);
  const [idleMins,    setIdleMins]      = useState(0);
  const [countdown,   setCountdown]     = useState(IDLE_CLOCKOUT_MIN - IDLE_WARNING_MIN);

  // ── record any user activity ────────────────────────────────────
  const onActivity = useCallback(() => {
    lastActivityRef.current = Date.now();

    // If we had auto-clocked them out and they come back → clock back in
    if (clockedOutRef.current) {
      clockedOutRef.current = false;
      setShowWarning(false);
      clockIn(staffId)
        .then(() => notification.success({
          message: 'Welcome back!',
          description: 'Your work session has resumed.',
          placement: 'bottomLeft',
        }))
        .catch(() => {}); // already clocked in is fine
    }

    // Dismiss warning if they clicked/moved
    setShowWarning(false);
  }, [staffId]);

  // ── attach global activity listeners ────────────────────────────
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, onActivity));
  }, [onActivity]);

  // ── also track tab visibility ────────────────────────────────────
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') onActivity();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [onActivity]);

  // ── main idle tick ───────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(async () => {
      // Close any stale session from a previous day before doing idle checks
      try {
        const stRes = await getCurrentStatus(staffId);
        const cin = stRes.data?.clock_in_time;
        if (cin && stRes.data?.current_status !== 'clocked_out') {
          const isToday = new Date(cin).toDateString() === new Date().toDateString();
          if (!isToday) { await clockOut(staffId); }
        }
      } catch { }

      const idleMs      = Date.now() - lastActivityRef.current;
      const idleMinutes = Math.floor(idleMs / 60000);
      setIdleMins(idleMinutes);

      if (idleMinutes >= IDLE_CLOCKOUT_MIN && !clockedOutRef.current) {
        // ── Auto clock-out ─────────────────────────────────────────
        clockedOutRef.current = true;
        setShowWarning(false);
        try {
          // Check if already clocked out
          const stRes = await getCurrentStatus(staffId);
          if (stRes.data?.current_status !== 'clocked_out') {
            await clockOut(staffId);
            notification.info({
              message: 'Auto Clocked Out',
              description: `You were inactive for ${IDLE_CLOCKOUT_MIN} minutes. Your session has been closed. Come back anytime to resume.`,
              placement: 'bottomLeft',
              duration: 8,
            });
          }
        } catch { }

      } else if (idleMinutes >= IDLE_WARNING_MIN && !clockedOutRef.current) {
        // ── Show warning popup ─────────────────────────────────────
        const remaining = IDLE_CLOCKOUT_MIN - idleMinutes;
        setCountdown(Math.max(remaining, 0));
        setShowWarning(true);
      } else if (idleMinutes < IDLE_WARNING_MIN) {
        setShowWarning(false);
      }
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [staffId]);

  // ── countdown ticker inside warning popup ───────────────────────
  useEffect(() => {
    if (!showWarning) return;
    const id = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(id); return 0; }
        return c - 1;
      });
    }, 60000);
    return () => clearInterval(id);
  }, [showWarning]);

  const handleStillWorking = () => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setIdleMins(0);
  };

  const handleClockOutNow = async () => {
    setShowWarning(false);
    clockedOutRef.current = true;
    try {
      await clockOut(staffId);
      notification.success({ message: 'Clocked out', placement: 'bottomLeft' });
    } catch { }
  };

  // pct of warning window used (30→90 min)
  const warningPct = Math.min(
    Math.round(((idleMins - IDLE_WARNING_MIN) / (IDLE_CLOCKOUT_MIN - IDLE_WARNING_MIN)) * 100),
    100
  );

  return (
    <Modal
      open={showWarning}
      closable={false}
      maskClosable={false}
      footer={null}
      centered
      width={400}
      bodyStyle={{ padding: '32px 28px', textAlign: 'center' }}
    >
      {/* Icon */}
      <div style={{
        width: 72, height: 72, borderRadius: '50%',
        background: 'linear-gradient(135deg, #faad14, #fa8c16)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 20px',
        boxShadow: '0 6px 20px rgba(250,173,20,0.35)',
      }}>
        <ClockCircleOutlined style={{ fontSize: 34, color: '#fff' }} />
      </div>

      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>
        Are you still working?
      </h2>
      <p style={{ color: '#595959', marginBottom: 20, lineHeight: 1.6 }}>
        You've been inactive for <strong>{idleMins} minutes</strong>.<br />
        Your session will auto-close in <strong>{countdown} minute{countdown !== 1 ? 's' : ''}</strong>.
      </p>

      {/* Progress bar */}
      <Progress
        percent={warningPct}
        showInfo={false}
        strokeColor={{ '0%': '#faad14', '100%': '#ff4d4f' }}
        trailColor="#f5f5f5"
        strokeWidth={8}
        style={{ marginBottom: 24 }}
      />

      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Button
          type="primary"
          size="large"
          icon={<CheckCircleOutlined />}
          block
          onClick={handleStillWorking}
          style={{ background: '#52c41a', borderColor: '#52c41a', borderRadius: 8, height: 44, fontSize: 15 }}
        >
          Yes, I'm still working
        </Button>
        <Button
          size="large"
          icon={<PoweroffOutlined />}
          block
          onClick={handleClockOutNow}
          danger
          style={{ borderRadius: 8, height: 44 }}
        >
          Clock out now
        </Button>
      </Space>

      <p style={{ marginTop: 16, fontSize: 12, color: '#bfbfbf' }}>
        Moving your mouse or typing also counts as activity
      </p>
    </Modal>
  );
}
